import { Date, PageWrapper, Search } from "@/components";
import * as GlobalS from "@/components/styled";
import { CONTENT_WIDTH, GUTTER_SIZE } from "@/enums";
import { routes } from "@/helpers";
import { useGetVpinProjectHistoryQuery } from "@/services";
import { Pager } from "@/sharedStyled";
import { Button, Col, Row, Table } from "antd";
import { useCallback, useEffect } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrayParam,
  NumberParam,
  StringParam,
  useQueryParams,
} from "use-query-params";

const PAGINATION_PAGE_SIZE = 10;
const DEFAULT_PAGE = 1;

interface IVpinHistory {
  id: string;
  projectId: string;
  milestoneName: string;
  milestoneAbbreviation: string;
  milestoneScheduleDesignator: string;
  message: string;
  milestoneId: string;
}

export const VpinHistory = () => {
  const { projectId } = useParams<string>();
  const intl = useIntl();

  const [queryParams, setQueryParams] = useQueryParams({
    page: NumberParam,
    pageSize: NumberParam,
    orderBy: ArrayParam,
    query: StringParam,
    fromDate: StringParam,
    toDate: StringParam,
  });

  const {
    data: { data, totalCount } = {},
    isFetching,
    isError,
  } = useGetVpinProjectHistoryQuery(
    {
      projectId,
      queryParameters: queryParams,
    },
    {
      skip: !projectId || !queryParams.page || !queryParams.pageSize,
    }
  );

  const setDefaultQuery = useCallback(() => {
    setQueryParams(
      { page: DEFAULT_PAGE, pageSize: PAGINATION_PAGE_SIZE },
      "push"
    );
  }, [setQueryParams]);

  useEffect(() => {
    setDefaultQuery();
  }, [setDefaultQuery]);

  useEffect(() => {
    if (!isError) return;

    toast.error(intl.formatMessage({ id: "general.toastSomethingWentWrong" }));
  }, [intl, isError]);

  const columns = [
    {
      title: intl.formatMessage({ id: "projectHistory.columns.created" }),
      dataIndex: "created",
      render: (data: string) => <Date value={data} showTime />,
      width: 175,
    },
    {
      title: intl.formatMessage({
        id: "projectHistory.columns.createdBy",
      }),
      dataIndex: "createdBy",
      width: 180,
      ellipsis: true,
    },
    {
      title: intl.formatMessage({
        id: "projectHistory.columns.milestone",
      }),
      render: ({
        milestoneId,
        milestoneName,
        milestoneAbbreviation,
        milestoneScheduleDesignator,
      }: IVpinHistory) =>
        milestoneId &&
        `${milestoneName} (${milestoneAbbreviation}) ${milestoneScheduleDesignator}`,
    },
    {
      title: intl.formatMessage({
        id: "projectHistory.columns.comment",
      }),
      dataIndex: "message",
    },
  ];

  if (!projectId) return null;

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({ id: "projectHistory.title" })}
      backButtonRoute={routes.projects.get(projectId)}
    >
      <Row className="pb-2">
        <Col
          xxl={CONTENT_WIDTH.oneThird}
          lg={CONTENT_WIDTH.half}
          md={CONTENT_WIDTH.twoThirds}
          xs={CONTENT_WIDTH.max}
        >
          <Search
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            loadingSearchResults={isFetching}
            clearAll={setDefaultQuery}
            searchTooltip={intl.formatMessage({
              id: "projectHistory.searchTooltip",
            })}
            searchPlaceholder={intl.formatMessage({
              id: "general.searchPlaceholder",
            })}
            parentComponentName="project-history-list"
          />
        </Col>
      </Row>
      <Row gutter={GUTTER_SIZE.large}>
        <Col span={CONTENT_WIDTH.max}>
          <GlobalS.PageContentCard
            title={"History"}
            extra={
              <Button
                data-test-id="view-viper-project-history-btn"
                type="primary"
              >
                <Link to={routes.projects.history(projectId)}>
                  {intl.formatMessage({
                    id: "projectHistory.viewViperHistory",
                  })}
                </Link>
              </Button>
            }
          >
            <Table
              data-test-id="project-vpin-history-table"
              columns={columns}
              dataSource={data}
              loading={isFetching}
              rowKey={(record: IVpinHistory) => record.id}
              pagination={false}
              scroll={{ x: 1200 }}
            />
            <Pager
              defaultCurrent={queryParams.page || DEFAULT_PAGE}
              current={queryParams.page || DEFAULT_PAGE}
              pageSize={queryParams.pageSize || PAGINATION_PAGE_SIZE}
              total={totalCount}
              onChange={(page, pageSize) =>
                setQueryParams({ page, pageSize: pageSize })
              }
              data-test-id="projects-vpin-history-table-pagination"
            />
          </GlobalS.PageContentCard>
        </Col>
      </Row>
    </PageWrapper>
  );
};
